import { useCallback, useMemo, useState } from 'react';
import { HighlightedRouteType, SuggestedSettingsType } from '@lp/features/RoutePlanner/types';
import { useAuthContext } from '@lp/core/auth';
import { useBenchmarkRoute } from '@lp/features/RoutePlanner/hooks/useBenchmarkRoute';
import { useLogisticsData } from '@lp/features/RoutePlanner/hooks/useLogisticsProjection';
import { RoutePlanStatus } from '@/api/apis/case';
import { RouteBuilderPermissions } from '@/permissions';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import { mergeCommercialWithMainRoute, mergeSurgeonAndMainRoute } from '@/utils/route';
import { RoutePlanType } from '@/types/route';
import {
  useApplySuggestedRoute,
  useRouteHighlight,
  useSuggestedRoutesLoadingState,
  useSuggestedRoutesState,
} from './hooks';
import { RoutePagination } from './RoutePagination';
import { BenchmarkRouteItem } from './BenchmarkRouteItem';
import { RouteItem } from './RouteItem';
import { RouteItemSpinner } from './RouteItemSpinner';
import { SuggestedChips, SuggestedRoutesSettings } from './SuggestedSettings';
import { SuggestedRoutesHeader } from './SuggestedRoutesHeader';
import {
  ApplyLoadingOverlay,
  NoRoutesFoundState,
  SurgeonTripsWarning,
  UnavailableResourcesWarning,
} from './SuggestedRoutesStatus';
import { SettingsButtons } from './SettingsButtons';
import { RouteItemActionButtons } from './RouteItemActionButtons';
import { SuggestedRouteValidationProvider } from './SuggestedRouteValidationContext';

type SuggestedRoutesType = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onHighlightRoute: (routePlan: HighlightedRouteType | null) => void;
};

export const SuggestedRoutes = ({ isOpen, onOpen, onClose, onHighlightRoute }: SuggestedRoutesType) => {
  const { checkPermission } = useAuthContext();
  const { caseId, organType, routePlanningStatus, serviceType, txCenterDetails } = useBaseCaseDetails();
  const { isTmdxDriveCompatible } = useLogisticsData();

  const [isRoutesLoading, setRouteLoading] = useState(false);
  const [routePlanIndex, setRoutePlanIndex] = useState(0);

  const [showSettings, setShowSettings] = useState(true);
  const [selectedSettings, setSelectedSettings] = useState<SuggestedSettingsType[]>([]);

  const { showBenchmark, isBenchmarkLoading, benchmarkPlan, setShowBenchmark, fetchBenchmark } = useBenchmarkRoute();
  const {
    isPlansLoaded,
    isSurgeonPlansLoaded,
    isCommercialsPlansLoaded,
    sortedRoutePlans,
    routePlans,
    surgeonPlans,
    commercialPlans,
    currentTaskId,
    currentGeneratedRouteType,
    generatedDateTimeMs,
    plansWarning,
    surgeonWarning,
    fetchRoutes,
    fetchAdditionalRoutes,
    setGeneratedRoutesStatistics,
  } = useSuggestedRoutesState();
  const { isApplyLoading, applyStatusText, failedApplyResourcesPlanId, appliedPlanId, applyRoute } =
    useApplySuggestedRoute();

  const generateRoutes = useCallback(
    async (list: SuggestedSettingsType[]) => {
      setRoutePlanIndex(0);
      setRouteLoading(true);

      const { taskId, planType, plans, forcedSurgeonId, aborted } = await fetchRoutes(list, isTmdxDriveCompatible);

      if (aborted) return;

      await setGeneratedRoutesStatistics({ caseId, isSuggestedRoutesGenerated: true }).catch(() => {});

      const planIds = Object.keys(plans);

      setRouteLoading(false);
      setShowSettings(!planIds.length);

      if (planIds.length) {
        await fetchAdditionalRoutes({ taskId, planType, plans, isTmdxDriveCompatible, forcedSurgeonId });
      }
    },
    [setGeneratedRoutesStatistics, fetchRoutes, fetchAdditionalRoutes, isTmdxDriveCompatible, caseId],
  );

  const handleApplySettings = useCallback(
    async (list: SuggestedSettingsType[]) => {
      setSelectedSettings(list);
      await generateRoutes(list);
    },
    [generateRoutes],
  );

  const handleSuggestRoutesOpen = async () => {
    onOpen();

    if (sortedRoutePlans.length) {
      return;
    }

    if (!benchmarkPlan && !isBenchmarkLoading && txCenterDetails) {
      await fetchBenchmark(isTmdxDriveCompatible);
    }
  };

  const setNextRoutePlan = useCallback(() => {
    const lastRoutePlanIndex = Math.max(sortedRoutePlans.length - 1, 0);
    setRoutePlanIndex((current) => Math.min(current + 1, lastRoutePlanIndex));
  }, [sortedRoutePlans.length]);

  const setPrevRoutePlan = useCallback(() => {
    setRoutePlanIndex((current) => Math.max(current - 1, 0));
  }, []);

  const routePlanMetadata = useMemo(() => {
    if (!sortedRoutePlans.length) return null;

    return sortedRoutePlans[routePlanIndex];
  }, [routePlanIndex, sortedRoutePlans]);

  // Reading the three slices out here keeps the merge off every unrelated streaming update.
  const { taskId: currentTaskKey, planIndex: currentPlanIndex } = routePlanMetadata ?? { taskId: '', planIndex: -1 };
  const currentTask = routePlans[currentTaskKey];
  const currentRoutePlanData = currentTask?.routes[currentPlanIndex];
  const currentSurgeonPlan = surgeonPlans[currentTaskKey]?.[currentPlanIndex];
  const currentCommercialPlan = commercialPlans[currentTaskKey]?.[currentPlanIndex];
  const currentOrTimeDiffMs = currentTask?.orTimeDiffMs ?? 0;

  const currentRoutePlan = useMemo<RoutePlanType | null>(() => {
    if (!currentRoutePlanData) return null;

    // mergeCommercialWithMainRoute handles a missing commercial plan, its parameter is just not optional.
    const mainRoute = mergeCommercialWithMainRoute(currentRoutePlanData, currentCommercialPlan!);
    const orTimeData = { orTimeDiffMs: currentOrTimeDiffMs };

    if (!currentSurgeonPlan) return { ...mainRoute, ...orTimeData };

    const mergedRoute = mergeSurgeonAndMainRoute(mainRoute, currentSurgeonPlan);

    return {
      ...mainRoute,
      ...mergedRoute,
      ...orTimeData,
    };
  }, [currentRoutePlanData, currentSurgeonPlan, currentCommercialPlan, currentOrTimeDiffMs]);

  const handleApply = useCallback(() => {
    if (!routePlanMetadata || !currentRoutePlan) return;

    applyRoute(currentRoutePlan, {
      taskId: routePlanMetadata.taskId,
      routeIndex: routePlanMetadata.planIndex,
      routeType: currentGeneratedRouteType,
    });
  }, [applyRoute, currentRoutePlan, routePlanMetadata, currentGeneratedRouteType]);

  const handleRegenerate = useCallback(() => generateRoutes(selectedSettings), [generateRoutes, selectedSettings]);

  useRouteHighlight({
    isOpen,
    currentRoutePlan,
    benchmarkPlan,
    isPlansLoaded,
    sortedRoutePlansCount: sortedRoutePlans.length,
    onHighlightRoute,
  });

  const isRoutePlanningStatus = routePlanningStatus === RoutePlanStatus.RoutePlanningStart;

  const { isDataLoading, isApplyAllowed, isPartialRoute, cleanUpTimeMs } = useSuggestedRoutesLoadingState({
    currentRoutePlan,
    currentGeneratedRouteType,
    serviceType,
    hasApplyPermission: checkPermission(RouteBuilderPermissions.AlgorithmApply),
    isRoutesLoading,
    isPlansLoaded,
    isSurgeonPlansLoaded,
    isCommercialsPlansLoaded,
    routePlansCount: sortedRoutePlans.length,
  });

  return (
    <div className="absolute z-404 drop-shadow-md bottom-0 left-1/2 -translate-x-1/2 flex flex-col items-center">
      <SuggestedRoutesHeader
        isOpen={isOpen}
        isDataLoading={isDataLoading}
        isRoutesLoading={isRoutesLoading}
        currentTaskId={currentTaskId}
        hasBenchmark={!!benchmarkPlan}
        hasRoutePlans={!!sortedRoutePlans.length}
        handleSuggestRoutesOpen={handleSuggestRoutesOpen}
        onClose={onClose}
      />
      {isOpen && (
        <SuggestedRouteValidationProvider
          key={currentTaskId}
          suggestedRouteId={currentRoutePlan?.id}
          isApplyAllowed={isApplyAllowed}
          isRoutePlanningStatus={isRoutePlanningStatus}
          isApplied={!!currentRoutePlan && currentRoutePlan.id === appliedPlanId}
          routeType={currentGeneratedRouteType}
          cleanUpTimeMs={cleanUpTimeMs}
        >
          <div className="w-240 relative flex flex-col gap-2 p-2 bg-white rounded-t-2xl">
            {showSettings && !isRoutesLoading && (
              <SuggestedRoutesSettings
                currentSettings={selectedSettings}
                canReset
                applySettings={handleApplySettings}
              />
            )}
            <div className="w-full px-2 py-3 bg-base-80 rounded-lg flex flex-col gap-2">
              <div className="w-full flex">
                <SettingsButtons
                  isDataLoading={isDataLoading}
                  showSettings={showSettings}
                  showBenchmark={showBenchmark}
                  hasBenchmark={!!benchmarkPlan}
                  setShowSettings={setShowSettings}
                  setShowBenchmark={setShowBenchmark}
                />
                {!!sortedRoutePlans.length && (
                  <>
                    <div className="flex justify-center items-center grow">
                      <RoutePagination
                        currentPage={routePlanIndex}
                        pages={sortedRoutePlans.length}
                        onPrevious={setPrevRoutePlan}
                        onNext={setNextRoutePlan}
                      />
                    </div>
                    <RouteItemActionButtons
                      isDataLoading={isDataLoading}
                      isApplyLoading={isApplyLoading}
                      generatedDateTimeMs={generatedDateTimeMs}
                      generateRoutes={handleRegenerate}
                      handleApplyRoute={handleApply}
                    />
                  </>
                )}
              </div>
              {!!currentRoutePlan && <SuggestedChips settings={selectedSettings} />}
            </div>
            {isRoutesLoading && <RouteItemSpinner />}
            {isPlansLoaded && !currentRoutePlan && <NoRoutesFoundState message={plansWarning} />}
            {!!currentRoutePlan && (
              <div className="w-full">
                {isApplyLoading && <ApplyLoadingOverlay statusText={applyStatusText} />}
                {!!routePlanMetadata && (
                  <>
                    {failedApplyResourcesPlanId === currentRoutePlan.id && <UnavailableResourcesWarning />}
                    {!!surgeonWarning && <SurgeonTripsWarning message={surgeonWarning} />}
                    <RouteItem
                      key={currentRoutePlan.id}
                      routePlan={currentRoutePlan}
                      organType={organType}
                      benchmarkMetrics={benchmarkPlan?.metrics}
                      orTimeUTC={routePlans[routePlanMetadata.taskId].orTimeUTC}
                      isPartialRoute={isPartialRoute}
                    />
                  </>
                )}
              </div>
            )}
            {showBenchmark && isBenchmarkLoading && <RouteItemSpinner />}
            {showBenchmark && !!benchmarkPlan && <BenchmarkRouteItem routePlan={benchmarkPlan} organType={organType} />}
          </div>
        </SuggestedRouteValidationProvider>
      )}
    </div>
  );
};
